export const userAccess = {
  data () {
    return {
      showDefaultView: true
    }
  },
  computed: {
    entities () {
      return ['document', 'document_party', 'document_file']
    },
    tenant () {
      return this.$auth.tenant
    },
    defaultFileView () {
      // Configure default view per tenant
      const tenantDefaults = {
        awg: 'table',
        'alliance-retail-group': 'explorer',
        pricechopper: 'table',
        pce: 'table',
        houchens: 'table',
        cosentino: 'table',
        spn: 'table',
        jbg: 'table',
        iprosystems: 'table',
      }
      return tenantDefaults[this.tenant] || 'table'
    },
    userData () {
      return this.$auth.user
    },
    userRoles () {
      return this.$auth.userRoles || []
    },
    userPermissions () {
      return this.$auth.permissions || []
    },
    userRelatedParties () {
      return this.$store.getters.userRelatedParties
    },
    userRelatedPartyIds () {
      return this.$store.getters.userRelatedPartyIds
    },
    activeUserRelatedParties () {
      return this.$store.getters.activeUserRelatedParties
    },
    allRetailersParty() {
      return this.$store.getters.allRetailersParty
    },
    ownerRetailerAdGroupIds () {
      return this.$store.getters.ownerRetailerAdGroupIds
    },
    userSearchPartyIds () {
      // if user has unrestricted store access, exclude stores to prevent overloading api
      // pce is an exception bc they only have ~50 stores max
      if (this.isRetailUser && !this.limitAccessByRelatedStores && this.$auth.tenant !== 'pricechopper') {
        const partyIds = (this.$auth.tenant === 'alliance-retail-group')
          ? [...this.nonStoreRelatedPartyIds, this.allRetailersParty?.id]
          : this.nonStoreRelatedPartyIds
        
        // For arg-owner-retailer, include AD group IDs for document access
        if (this.isARGOwner && this.ownerRetailerAdGroupIds.length > 0) {
          return [...partyIds, ...this.ownerRetailerAdGroupIds]
        }
        return partyIds
      }
      if (this.isVendorUser) {
        return this.$auth.tenant === 'alliance-retail-group'
          ? [...this.userVendorPartyIds, ...this.userSubvendorPartyIds]
          : this.userVendorPartyIds
      }
      
      // For arg-owner-retailer with limited access, include AD group IDs
      if (this.isARGOwner && this.ownerRetailerAdGroupIds.length > 0) {
        return [...this.userRelatedPartyIds, ...this.ownerRetailerAdGroupIds]
      }
      
      return this.userRelatedPartyIds
    },
    userStores () {
      return this.userRelatedParties.flatMap(party => {
        return party?.party_type_constant === 'STORE' ? party : []
      })
    },
    userStorePartyIds () {
      return this.userRelatedParties.flatMap(party => {
        return party?.party_type_constant === 'STORE' ? party.id : []
      })
    },
    userOwners () {
      return this.userRelatedParties.flatMap(party => {
        return party?.party_type_constant === 'OWNER' ? party : []
      })
    },
    userAdGroupPartyIds () {
      return this.userRelatedParties.flatMap(party => {
        return party?.party_type_constant === 'AD_GROUP'
          ? party.id
          : []
      })
    },
    userSubvendorPartyIds () {
      return this.userRelatedParties.flatMap(party => {
        return party?.party_type_constant === 'SUBVENDOR' ? party.id : []
      })
    },
    userVendorPartyIds () {
      return this.userRelatedParties.flatMap(party => {
        return party?.party_type_constant === 'VENDOR' ? party.id : []
      })
    },
    nonStoreRelatedPartyIds () {
      return this.userRelatedPartyIds.filter(id => id && this.userStorePartyIds.includes(id) === false)
    },
    hasPortalAccess () {
      if ((this.$auth.tenant === 'alliance-retail-group') && (process.env.NODE_ENV === 'production') && this.noProdAccess) {
        return false
      }
      const readScopes = this.entities.map(entity => `${entity}:read`)
      return readScopes.every(scope => this.hasScope(scope))
    },
    userAccessiblePortals() {
      const portals = ['ad_planner', 'customer', 'promo', 'item', 'billing', 'tpr', 'partner']
      const isProd = process.env.NODE_ENV === 'production'

      return portals.filter(portal => {
        let readScope = `${portal}:read`
        if (portal === 'billing') {
          readScope = 'billing_batch:read'
        } else if (portal === 'customer') {
          readScope = 'party:read'
        } else if (portal === 'tpr') {
          readScope = 'tpr_batch:read'
        } else if (portal === 'partner') {
          readScope = 'document:read'
        }
        const canRead = this.hasScope(readScope)
        if (!canRead) return false

        // Hard deny in all envs
        if (this.hasScope(`${portal}:no_access`)) return false

        // Deny in prod only (dev allowed) if they have the prod-block flag
        if (isProd && this.hasScope(`${portal}:no_access_prod`)) return false

        return true
      })
    },
    noProdAccess() {
      return this.userPermissions.includes('partner:no_access_prod')
    },
    isAdminUser () {
      return this.$auth.isAdminUser
    },
    edgeManagerRole() {
      return ["ipro-portal.retailer-edge-manager"]
    },
    isEdgeManager() {
      return this.userRoles.some((role) => this.edgeManagerRole.includes(role))
    },
    isARGRetailer() {
      return this.userRoles.includes('arg-store-retailer') || this.userRoles.includes('arg-owner-retailer')
    },
    hasAdminPartyAccess () {
      return this.hasScope('partner:admin_parties')
    },
    isNonAdminUser () {
      return (!this.isAdminUser && !this.hasAdminPartyAccess)
    },
    hasOrderGridAccess () {
      if (this.isAdminUser) return true
      return ['pricechopper', 'alliance-retail-group'].includes(this.$auth.tenant)
        ? this.hasScope('partner:order_grid') 
        : this.hasRetailWriteAccess
    },
    hasOrderGridReadOnlyAccess () {
      return this.tenant === 'alliance-retail-group' && this.hasScope('order_grid:read_only') && this.hasOrderGridAccess
    },
    hasAdPlanEditAccess () {
      return this.tenant === 'alliance-retail-group' && (this.isAdPlanEditUser || this.isAdminUser)
    },
    hasDocumentUploadAccess () {
      return this.isAdminUser || this.hasScope('document:upload')
    },
    hasNotificationsAccess () {
      if (this.tenant === 'jbg') return this.userRoles.includes('jbg-admin')
      if (this.tenant === 'awg') return this.userRoles.includes('mobile.admin')
      return false
    },
    storesOnlyAudience () {
      return this.tenant === 'awg' && this.userRoles.includes('mobile.admin')
    },
    hasWriteAccess () {
      return this.$auth.hasWriteAccess
    },
    hasRetailWriteAccess () {
      const scopes = ['ad_planner:create', 'ad_planner:update']
      return scopes.every(scope => this.hasScope(scope))
    },
    limitAccessByRelatedStores () {
      return this.hasScope('retail:related_stores')
    },
    isRetailUser () {
      return this.isNonAdminUser && this.userRoles.some(roleName => {
        return roleName?.toLowerCase()?.includes('retail')
         || this.isPromoCoordinator
         || this.isGroupManager
      })
    },
    isVendorUser () {
      return this.isNonAdminUser && this.userRoles.some(roleName => {
        return roleName?.toLowerCase()?.includes('vendor')
      }) && !this.isPromoCoordinator
    },
    isPromoCoordinator () {
      return (this.isNonAdminUser && this.userRoles.includes('promo.coordinator'))
    },
    isJBGRetailer () {
      return (this.$auth.tenant === 'jbg') && !this.isAdminUser && this.userRoles.includes('jbg-retailer')
    },
    isPCEOwner () {
      return (this.$auth.tenant === "pricechopper") && !this.isAdminUser && this.userRoles.includes('pce-owner')
    },
    isARGOwner () {
      return (this.$auth.tenant === "alliance-retail-group") && !this.isAdminUser && this.userRoles.includes('arg-owner-retailer')
    },
    isGroupManager () {
      return (this.$auth.tenant === "alliance-retail-group") && !this.isAdminUser && this.userRoles.includes('arg-group-manager')
    },
    isProductCatalogRetailer () {
      return this.isJBGRetailer && this.userStores.some(store => {
        return (store?.attributes?.PRODUCT_CATALOG_STORE === 'True')
      })
    },
    categories () {
      return this.$store.getters.categories
    },
    retailCategories () {
      return this.$store.getters.retailCategories
    },
    vendorCategories () {
      return this.$store.getters.vendorCategories
    },
    adminCategories () {
      return this.$store.getters.adminCategories
    },
    userCategories () {
      if (['pricechopper', 'alliance-retail-group'].includes(this.$auth.tenant)) {
        return this.scopedCategories
      }
      if (this.isRetailUser) {
        return this.isPromoCoordinator
          ? [...this.retailCategories, ...this.vendorCategories]
          : this.retailCategories
      } else if (this.isVendorUser) {
        return this.vendorCategories
      }
      return this.categories
    },
    scopedCategories () {
      if (!this.isAdminUser) {
        const categories = []
        if (this.hasScope('document:retailer_report')) {
          categories.push(...this.retailCategories)
        }
        if (this.hasScope('document:vendor_report')) {
          categories.push(...this.vendorCategories)
        }
        if (this.hasScope('document:admin_report')) {
          categories.push(...this.adminCategories)
        }
        return categories
      }
      return this.categories
    },
    tenantRoleRestrictedSubcats () {
      return {
        pricechopper: {
          'arg-admin': ['RETAILER_AD_PLANNER', 'RETAILER_ORDER_HISTORY', 'RETAILER_TPR_REPORT', 'RETAILER_PCE_POSTED'],
          'pce-ad-builder': ['RETAILER_ORDER_HISTORY'],
          'pce-marketing-partner': ['RETAILER_ORDER_HISTORY', 'RETAILER_TPR_REPORT', 'RETAILER_BILLING_REPORT', 'AD_GROUP_BILLING_REPORT'],
          'pce-store-retailer': ['AD_GROUP_BILLING_REPORT'],
          'pce-ad-group-retailer': ['AD_GROUP_BILLING_REPORT']
        },
        jbg: {
          'jbg-retailer': ['RETAILER_BILLING_REPORT']
        },
        'alliance-retail-group': {
          'arg-vendor': ['VENDOR_SRP_ALERT', 'VENDOR_AUDIT_REPORT', 'VENDOR_FINAL_INVOICE_DETAIL_REPORT']
        }
      }
    },
    userRestrictedSubcategories () {
      if (this.isAdminUser) return []
      const roleExclusions = this.tenantRoleRestrictedSubcats[this.tenant]
      if (roleExclusions) {
        const excludedSubcats = Object.entries(roleExclusions).flatMap(([role, subcats]) => {
          return (this.userRoles.includes(role)) ? subcats : []
        })
        if (this.$auth.tenant === 'alliance-retail-group' && !this.hasDealFileAccess) {
          excludedSubcats.push('RETAILER_HOSTING')
        }
        return excludedSubcats
      }
      return []
    },
    dealFileOwnerGroupCodes() {
      return this.$store.getters.dealFileOwnerGroupCodes
    },
    hasDealFileOwnerRelationship() {
      return this.userOwners.some(party => {
        return this.dealFileOwnerGroupCodes.includes(party?.attributes?.OWNER_GROUP_CODE)
      })
    },
    hasDealFileAccess() {
      return (this.$auth.tenant === 'alliance-retail-group') 
        && (this.isAdminUser || this.hasDealFileOwnerRelationship)
    }
  },
  methods: {
    hasScope (scope) {
      return this.userPermissions.includes(scope)
    },
  }
}
